import { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { APIContext } from "../../../APIContext";
import { db } from "../../../lib/firebase";

/**
 * Product Provisioner — Function 1 wizard (Phase 2, milestone M4). Launches a new custom
 * client Product: the products/{FPID} doc (with editable descriptor fields), the Shopify
 * Page (bespoke template `page.<SPID>`) + embed/page_type metafields, the employee
 * `custom-list` menu item, and (optionally) the client Grant. Runs preview → confirm →
 * create; editing any field after a preview invalidates it, so an employee can never
 * confirm a stale plan.
 *
 * The three client-facing THEME edits (bespoke template + page gate + client nav link) are
 * shown in the preview and returned by create, but written to the LIVE theme only when a
 * developer clicks "Apply theme edits" (→ applyThemeEdits). See
 * docs/plans/phase-2-product-provisioner.md.
 */

/** URL-safe slug — must match the backend's slugify (provisioner.ts) exactly. */
function slugify(input: string): string {
  return String(input || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "");
}

interface ICompany {
  docId: string;
  name: string;
}

// Field hint shown BETWEEN the label and the input (its own line, muted, small).
const HINT = "text-muted";
const HINT_STYLE = { fontSize: "0.85em", display: "block", marginBottom: "0.35rem" } as const;

export default function NewProductPage() {
  const context = useContext(APIContext);

  const [companies, setCompanies] = useState<ICompany[]>([]);
  const [companyId, setCompanyId] = useState<string>("");
  const [dashboardName, setDashboardName] = useState<string>("");
  const [embedUrl, setEmbedUrl] = useState<string>("");

  // Optional product-doc descriptor fields. Pre-filled with the "Custom" convention every
  // custom product doc uses; editable, and clearing one omits it from the doc (backend).
  const [collectionField, setCollectionField] = useState<string>("");
  const [countryField, setCountryField] = useState<string>("");
  const [industryField, setIndustryField] = useState<string>("");
  const [productTypeField, setProductTypeField] = useState<string>("");

  // Derived identifiers, editable before submit. `null` = follow the auto-derived
  // value; a string = the employee has overridden it.
  const [spidOverride, setSpidOverride] = useState<string | null>(null);
  const [handleOverride, setHandleOverride] = useState<string | null>(null);

  const [preview, setPreview] = useState<any | null>(null);
  const [result, setResult] = useState<any | null>(null);
  const [busy, setBusy] = useState<"" | "preview" | "create">("");
  const [status, setStatus] = useState<{ type: string; msg: string } | null>(null);

  // Product category — default "Custom"; a standard product overrides it (e.g. "Product Brand Health").
  const [category, setCategory] = useState<string>("");

  // Optional embed-display metafields (blank = not set). The theme applies a fixed width only
  // when the paired height is also set.
  const [pageDisplayTitle, setPageDisplayTitle] = useState<string>("");
  const [iframeWidth, setIframeWidth] = useState<string>("");
  const [iframeHeight, setIframeHeight] = useState<string>("");
  const [wrapperWidth, setWrapperWidth] = useState<string>("");
  const [wrapperHeight, setWrapperHeight] = useState<string>("");
  // "Make public" = the visibility_override==1 gate branch (visible to any signed-in client).
  const [makePublic, setMakePublic] = useState<boolean>(false);

  // Access model: "own_tag" (mint access:<SPID> + grant) or "shared_tags" (complement — reuse
  // existing products' access; no new tag, no grant). Source products are chosen by name; we send
  // their access tags (shopify_product_id).
  const [accessMode, setAccessMode] = useState<"own_tag" | "shared_tags">("own_tag");
  const [products, setProducts] = useState<{ docId: string; name: string; tag: string }[]>([]);
  const [sourceProductTags, setSourceProductTags] = useState<string[]>([]);
  const [navAnchorTag, setNavAnchorTag] = useState<string>(""); // which source product's menu section
  const [navLabel, setNavLabel] = useState<string>("");

  // Theme-apply is a SEPARATE developer-gated step after create (writes the 3 assets to Live).
  const [themeBusy, setThemeBusy] = useState<boolean>(false);
  const [themeResult, setThemeResult] = useState<any | null>(null);

  // Optional post-create step: add a resource button (→ this new page) on source dashboard page(s).
  const [resourceHandlesText, setResourceHandlesText] = useState<string>("");
  const [resourceDisplayText, setResourceDisplayText] = useState<string>("Data Download");
  const [resourceSlot, setResourceSlot] = useState<string>("auto");
  const [resourceBusy, setResourceBusy] = useState<"" | "preview" | "set">("");
  const [resourcePreview, setResourcePreview] = useState<any | null>(null);
  const [resourceResult, setResourceResult] = useState<any | null>(null);

  useEffect(() => {
    const companiesQuery = query(collection(db, "companies"), orderBy("company_name", "asc"));
    const unsub = onSnapshot(
      companiesQuery,
      (snap) =>
        setCompanies(
          snap.docs
            .filter((d) => d.data().company_name)
            .map((d) => ({ docId: d.id, name: d.data().company_name }))
        ),
      (err) => console.error(err)
    );
    return () => unsub();
  }, []);

  // Existing products, for the "Complement" source-product picker (name → access tag).
  useEffect(() => {
    const productsQuery = query(collection(db, "products"), orderBy("product_name", "asc"));
    const unsub = onSnapshot(
      productsQuery,
      (snap) =>
        setProducts(
          snap.docs
            .filter((d) => d.data().product_name && d.data().shopify_product_id)
            .map((d) => ({ docId: d.id, name: d.data().product_name, tag: d.data().shopify_product_id }))
        ),
      (err) => console.error(err)
    );
    return () => unsub();
  }, []);

  const companyName = useMemo(
    () => companies.find((c) => c.docId === companyId)?.name || "",
    [companies, companyId]
  );

  const derivedSpid = useMemo(
    () => (companyName && dashboardName ? `custom-${slugify(companyName)}-${slugify(dashboardName)}` : ""),
    [companyName, dashboardName]
  );

  const spid = spidOverride === null ? derivedSpid : spidOverride;
  // Handle defaults to the SPID (matches the backend + the live template==SPID==tag
  // invariant); still overridable.
  const handle = handleOverride === null ? spid : handleOverride;

  // Any input change invalidates a shown preview/result — you can't confirm a stale plan.
  const invalidate = () => {
    if (preview) setPreview(null);
    if (result) setResult(null);
    if (themeResult) setThemeResult(null);
    if (status) setStatus(null);
  };

  const canPreview = Boolean(companyId && dashboardName.trim() && embedUrl.trim()) && !busy;

  const sourceTags = sourceProductTags;
  const isShared = accessMode === "shared_tags";
  // The chosen source products (for the "where should the menu link go?" picker).
  const selectedSourceProducts = useMemo(
    () => products.filter((p) => sourceProductTags.includes(p.tag)),
    [products, sourceProductTags]
  );

  const payload = () => ({
    company_id: companyId,
    dashboard_name: dashboardName.trim(),
    embed_url: embedUrl.trim(),
    // The wizard never grants inline — access is given afterward via the New Access screen.
    grant_now: false,
    spid: spid || undefined,
    handle: handle || undefined,
    // Descriptor fields sent as-is; the backend trims and omits any that are blank.
    category: category,
    collection: collectionField,
    country: countryField,
    industry: industryField,
    product_type: productTypeField,
    // Optional embed-display metafields (backend sets only the non-blank ones).
    page_display_title: pageDisplayTitle,
    iframe_width: iframeWidth,
    iframe_height: iframeHeight,
    wrapper_width: wrapperWidth,
    wrapper_height: wrapperHeight,
    visibility_override: makePublic ? "1" : "",
    // Access model.
    access_mode: accessMode,
    source_tags: sourceTags,
    nav_anchor_tag: navAnchorTag || undefined,
    nav_label: navLabel.trim() || undefined,
  });

  const runPreview = async () => {
    setBusy("preview");
    setStatus(null);
    setResult(null);
    try {
      const res: any = await context.previewCreateProduct(payload());
      const d = res?.data || {};
      if (d.status === "success") {
        setPreview(d.plan);
      } else {
        setStatus({ type: "danger", msg: d.message || "Preview failed." });
      }
    } catch (err: any) {
      console.error(err);
      setStatus({ type: "danger", msg: err?.message || "Unexpected error." });
    } finally {
      setBusy("");
    }
  };

  const runCreate = async () => {
    if (!preview) return;
    setBusy("create");
    setStatus(null);
    setThemeResult(null);
    try {
      const res: any = await context.createProduct(payload());
      const d = res?.data || {};
      setResult(d);
      setStatus({
        type: d.status === "success" ? "success" : "danger",
        msg: d.message || (d.status === "success" ? "Created." : "Create failed."),
      });
      if (d.status === "success") setPreview(null);
    } catch (err: any) {
      console.error(err);
      setStatus({ type: "danger", msg: err?.message || "Unexpected error." });
    } finally {
      setBusy("");
    }
  };

  // Developer-gated: write the 3 theme assets to the LIVE theme (with a rollback backup).
  const runApplyTheme = async () => {
    if (!result?.spid) return;
    setThemeBusy(true);
    setStatus(null);
    try {
      const res: any = await context.applyThemeEdits({
        spid: result.spid,
        handle: result.handle,
        dashboard_name: dashboardName.trim(),
        // Carry the access model so the theme write uses the matching (own-tag vs complement) builders.
        access_mode: accessMode,
        source_tags: sourceTags,
        nav_anchor_tag: navAnchorTag || undefined,
        nav_label: navLabel.trim() || undefined,
      });
      const d = res?.data || {};
      setThemeResult(d);
      setStatus({
        type: d.status === "success" ? "success" : "danger",
        msg: d.message || (d.status === "success" ? "Theme edits applied." : "Apply failed."),
      });
    } catch (err: any) {
      console.error(err);
      setStatus({ type: "danger", msg: err?.message || "Unexpected error." });
    } finally {
      setThemeBusy(false);
    }
  };

  // Optional post-create: add a resource button (→ this new page) on source dashboard page(s).
  const resourceHandles = useMemo(
    () => resourceHandlesText.split(/[\n,]/).map((s) => s.trim()).filter(Boolean),
    [resourceHandlesText]
  );
  const runResource = async (dryRun: boolean) => {
    if (!result?.handle || !resourceHandles.length) return;
    setResourceBusy(dryRun ? "preview" : "set");
    setStatus(null);
    try {
      const res: any = await context.setSourceResourceLink({
        page_handles: resourceHandles,
        display_text: resourceDisplayText.trim() || "Data Download",
        link: `/pages/${result.handle}`,
        slot: resourceSlot,
        dry_run: dryRun,
      });
      const d = res?.data || {};
      if (dryRun) setResourcePreview(d);
      else {
        setResourceResult(d);
        setResourcePreview(null);
      }
      if (d.status === "error") setStatus({ type: "danger", msg: d.message || "Resource step failed." });
    } catch (err: any) {
      console.error(err);
      setStatus({ type: "danger", msg: err?.message || "Unexpected error." });
    } finally {
      setResourceBusy("");
    }
  };

  return (
    <div style={{ fontFamily: "Montserrat, sans-serif", marginTop: "3rem" }}>
      <div className="m0 listings-wrapper middle half-width">
        <h1 style={{ fontWeight: "bold" }}>Launch a New Product</h1>
        <p>
          Set up a new client dashboard page from start to finish. Fill in the form, then click{" "}
          <strong>Preview</strong> to see exactly what will happen — <strong>nothing is saved
          until you confirm</strong>. Fields marked <span className="text-danger">*</span> are required.
        </p>

        {/* Company */}
        <div style={{ marginTop: "1rem" }} className="form-group">
          <label htmlFor="np-company">
            Client company <span className="text-danger">*</span>
          </label>
          <div className={HINT} style={HINT_STYLE}>
            Which client is this dashboard for? (If it's a shared page for many clients, just pick any
            one — you'll choose who sees it further down.)
          </div>
          <select
            id="np-company"
            className="form-control"
            value={companyId}
            onChange={(e) => {
              setCompanyId(e.target.value);
              invalidate();
            }}
          >
            <option value="">&mdash; choose a client &mdash;</option>
            {companies.map((c) => (
              <option key={c.docId} value={c.docId}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        {/* Dashboard name */}
        <div style={{ marginTop: "1rem" }} className="form-group">
          <label htmlFor="np-name">
            Dashboard name <span className="text-danger">*</span>
          </label>
          <div className={HINT} style={HINT_STYLE}>The name of this dashboard, the way you'd say it out loud.</div>
          <input
            id="np-name"
            type="text"
            className="form-control"
            placeholder="e.g. Oral Pouch Brand Tracker"
            value={dashboardName}
            onChange={(e) => {
              setDashboardName(e.target.value);
              invalidate();
            }}
          />
        </div>

        {/* Embed URL */}
        <div style={{ marginTop: "1rem" }} className="form-group">
          <label htmlFor="np-url">
            Dashboard web link <span className="text-danger">*</span>
          </label>
          <div className={HINT} style={HINT_STYLE}>
            The web address of the dashboard that's already been built (a Power BI or Netlify link).
            This page just displays it — it doesn't build it.
          </div>
          <input
            id="np-url"
            type="url"
            className="form-control"
            placeholder="https://brightfield-windriver.netlify.app"
            value={embedUrl}
            onChange={(e) => {
              setEmbedUrl(e.target.value);
              invalidate();
            }}
          />
        </div>

        {/* Derived, editable identifiers */}
        <div style={{ marginTop: "1rem" }} className="form-group">
          <label htmlFor="np-spid">Internal ID</label>
          <div className={HINT} style={HINT_STYLE}>
            A short unique code that identifies this product behind the scenes (it's also the product's
            access tag). Filled in automatically from the client + dashboard name — only change it if it
            has to match a specific value. Examples already in use:{" "}
            <code>US_Cannabis_Brand_Health_Product</code>, <code>custom-curaleaf-brand-health</code>,{" "}
            <code>custom-wind-river-oral-pouch</code>.
          </div>
          <input
            id="np-spid"
            type="text"
            className="form-control"
            placeholder="e.g. custom-curaleaf-brand-health"
            value={spid}
            onChange={(e) => {
              setSpidOverride(e.target.value);
              invalidate();
            }}
          />
        </div>
        <div style={{ marginTop: "1rem" }} className="form-group">
          <label htmlFor="np-handle">Page address</label>
          <div className={HINT} style={HINT_STYLE}>
            The end of the page's web link — clients open it at{" "}
            app.brightfieldgroup.com/pages/<strong>{handle || "…"}</strong>. Filled in automatically; you
            usually don't need to change it.
          </div>
          <input
            id="np-handle"
            type="text"
            className="form-control"
            placeholder="e.g. us-cannabis-brand-health-product-data-download"
            value={handle}
            onChange={(e) => {
              setHandleOverride(e.target.value);
              invalidate();
            }}
          />
        </div>

        {/* Product-doc descriptor fields (labels). Optional but preferred; placeholders suggest
            already-used values. Blank ones are omitted (category falls back to "Custom"). */}
        <div style={{ marginTop: "1.5rem", borderTop: "1px dashed #eee", paddingTop: "1rem" }}>
          <strong>Labels</strong>{" "}
          <span className="text-muted">
            (used for internal grouping and reporting — optional, but we prefer to fill them in)
          </span>
        </div>
        <div style={{ marginTop: "0.5rem" }} className="form-group">
          <label htmlFor="np-collection">Collection</label>
          <div className={HINT} style={HINT_STYLE}>The group this dashboard belongs to.</div>
          <input
            id="np-collection"
            type="text"
            className="form-control"
            placeholder="Custom, US Cannabis, Canada Cannabis…"
            value={collectionField}
            onChange={(e) => {
              setCollectionField(e.target.value);
              invalidate();
            }}
          />
        </div>
        <div style={{ marginTop: "1rem" }} className="form-group">
          <label htmlFor="np-country">Country</label>
          <div className={HINT} style={HINT_STYLE}>Which country the data covers.</div>
          <input
            id="np-country"
            type="text"
            className="form-control"
            placeholder="Custom, U.S., Canada…"
            value={countryField}
            onChange={(e) => {
              setCountryField(e.target.value);
              invalidate();
            }}
          />
        </div>
        <div style={{ marginTop: "1rem" }} className="form-group">
          <label htmlFor="np-industry">Industry</label>
          <div className={HINT} style={HINT_STYLE}>The industry this dashboard is about.</div>
          <input
            id="np-industry"
            type="text"
            className="form-control"
            placeholder="Custom, Cannabis, CBD…"
            value={industryField}
            onChange={(e) => {
              setIndustryField(e.target.value);
              invalidate();
            }}
          />
        </div>
        <div style={{ marginTop: "1rem" }} className="form-group">
          <label htmlFor="np-product-type">Product type</label>
          <div className={HINT} style={HINT_STYLE}>A general type tag for the product.</div>
          <input
            id="np-product-type"
            type="text"
            className="form-control"
            placeholder="custom"
            value={productTypeField}
            onChange={(e) => {
              setProductTypeField(e.target.value);
              invalidate();
            }}
          />
        </div>
        <div style={{ marginTop: "1rem" }} className="form-group">
          <label htmlFor="np-category">Category</label>
          <div className={HINT} style={HINT_STYLE}>
            The kind of report this is. If you leave it blank, it's saved as "Custom".
          </div>
          <input
            id="np-category"
            type="text"
            className="form-control"
            placeholder="Custom, Product Brand Health, Retail Brand Health…"
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
              invalidate();
            }}
          />
        </div>

        {/* Embed display (optional) */}
        <div style={{ marginTop: "1.5rem", borderTop: "1px dashed #eee", paddingTop: "1rem" }}>
          <strong>How the dashboard looks</strong>{" "}
          <span className="text-muted">
            (optional — a heading and the size of the dashboard box; leave blank to use defaults)
          </span>
          <div style={{ marginTop: "0.5rem" }} className="form-group">
            <label htmlFor="np-display-title">Heading shown on the page</label>
            <div className={HINT} style={HINT_STYLE}>The title that appears above the dashboard.</div>
            <input
              id="np-display-title"
              type="text"
              className="form-control"
              placeholder="e.g. Data Download - Product Brand Health"
              value={pageDisplayTitle}
              onChange={(e) => {
                setPageDisplayTitle(e.target.value);
                invalidate();
              }}
            />
          </div>
          <label style={{ marginTop: "0.5rem", display: "block" }}>Size of the dashboard box (in pixels)</label>
          <div className={HINT} style={HINT_STYLE}>
            "Dashboard" is the report itself; "Window" is the visible frame around it (often a little
            shorter, to hide the scrollbar). We usually use width 1290. Leave blank if you're not sure —
            it's easy to adjust later.
          </div>
          <div style={{ display: "flex", gap: "1rem" }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="np-iframe-w" className="text-muted" style={{ fontSize: "0.85em" }}>Dashboard width</label>
              <input id="np-iframe-w" type="text" className="form-control" placeholder="1290"
                value={iframeWidth} onChange={(e) => { setIframeWidth(e.target.value); invalidate(); }} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="np-iframe-h" className="text-muted" style={{ fontSize: "0.85em" }}>Dashboard height</label>
              <input id="np-iframe-h" type="text" className="form-control" placeholder="1096"
                value={iframeHeight} onChange={(e) => { setIframeHeight(e.target.value); invalidate(); }} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="np-wrap-w" className="text-muted" style={{ fontSize: "0.85em" }}>Window width</label>
              <input id="np-wrap-w" type="text" className="form-control" placeholder="1290"
                value={wrapperWidth} onChange={(e) => { setWrapperWidth(e.target.value); invalidate(); }} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="np-wrap-h" className="text-muted" style={{ fontSize: "0.85em" }}>Window height</label>
              <input id="np-wrap-h" type="text" className="form-control" placeholder="1020"
                value={wrapperHeight} onChange={(e) => { setWrapperHeight(e.target.value); invalidate(); }} />
            </div>
          </div>
        </div>

        {/* Access model */}
        <div style={{ marginTop: "1.5rem", borderTop: "1px dashed #eee", paddingTop: "1rem" }}>
          <div className="form-group">
            <label>Who can see this page?</label>
            <div className={HINT} style={HINT_STYLE}>
              {isShared
                ? "Pick the existing dashboards below. Any client who already has one of them will see this new page automatically — nothing else to do."
                : "This page is private to the client chosen above. You can give them access now (below), or later from the usual New Access screen."}
            </div>
            <div className="form-check">
              <input
                type="radio"
                id="np-mode-own"
                name="np-access-mode"
                className="form-check-input"
                value="own_tag"
                checked={accessMode === "own_tag"}
                onChange={() => {
                  setAccessMode("own_tag");
                  invalidate();
                }}
              />
              <label htmlFor="np-mode-own" className="form-check-label">
                <strong>Just one client</strong> — only the client chosen above
              </label>
            </div>
            <div className="form-check">
              <input
                type="radio"
                id="np-mode-shared"
                name="np-access-mode"
                className="form-check-input"
                value="shared_tags"
                checked={accessMode === "shared_tags"}
                onChange={() => {
                  setAccessMode("shared_tags");
                  invalidate();
                }}
              />
              <label htmlFor="np-mode-shared" className="form-check-label">
                <strong>Everyone who already has certain other dashboards</strong> (a shared add-on)
              </label>
            </div>
          </div>

          {isShared && (
            <>
              <div className="form-group" style={{ marginTop: "1rem" }}>
                <label htmlFor="np-source-products">
                  Show this page to clients who have…{" "}
                  <span className="text-muted">(pick one or more dashboards)</span>
                </label>
                <div className={HINT} style={HINT_STYLE}>
                  Hold Ctrl (or ⌘ on Mac) to pick more than one. {sourceProductTags.length} selected.
                </div>
                <select
                  id="np-source-products"
                  className="form-control"
                  multiple
                  size={Math.min(8, Math.max(4, products.length))}
                  value={sourceProductTags}
                  onChange={(e) => {
                    const picked = Array.from(e.target.selectedOptions).map((o) => o.value);
                    setSourceProductTags(picked);
                    if (navAnchorTag && !picked.includes(navAnchorTag)) setNavAnchorTag("");
                    invalidate();
                  }}
                >
                  {products.map((p) => (
                    <option key={p.docId} value={p.tag}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group" style={{ marginTop: "1rem" }}>
                <label htmlFor="np-nav-anchor-tag">Where should the menu link go?</label>
                <div className={HINT} style={HINT_STYLE}>
                  The new page's link appears in the client menu right under the dashboard you choose. Leave
                  as "don't add a menu link" if clients will reach it another way (like a button).
                </div>
                <select
                  id="np-nav-anchor-tag"
                  className="form-control"
                  value={navAnchorTag}
                  onChange={(e) => {
                    setNavAnchorTag(e.target.value);
                    invalidate();
                  }}
                >
                  <option value="">— don't add a menu link —</option>
                  {selectedSourceProducts.map((p) => (
                    <option key={p.tag} value={p.tag}>
                      Under "{p.name}"
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group" style={{ marginTop: "1rem" }}>
                <label htmlFor="np-nav-label">
                  Menu link text <span className="text-muted">(defaults to the dashboard name above)</span>
                </label>
                <input
                  id="np-nav-label"
                  type="text"
                  className="form-control"
                  placeholder="Product Data Download"
                  value={navLabel}
                  onChange={(e) => {
                    setNavLabel(e.target.value);
                    invalidate();
                  }}
                />
              </div>
            </>
          )}

          {/* Make public (visibility_override) */}
          <div style={{ marginTop: "1rem" }} className="form-check">
            <input
              id="np-public"
              type="checkbox"
              className="form-check-input"
              checked={makePublic}
              onChange={(e) => {
                setMakePublic(e.target.checked);
                invalidate();
              }}
            />
            <label htmlFor="np-public" className="form-check-label">
              &nbsp;Make this page visible to <strong>every signed-in client</strong> (ignores the access
              rules above — use only for truly public pages)
            </label>
          </div>
        </div>

        {/* Access is granted AFTER create, via the existing New Access screen — a link appears on
            the result panel below. (Complement pages need no grant — they show for source-product
            holders automatically.) */}

        {/* Preview action */}
        <div style={{ display: "flex", marginTop: "1.5rem", alignItems: "center" }}>
          {status && (
            <div
              className={`alert alert-${status.type}`}
              style={{ marginRight: "1rem", marginBottom: 0 }}
            >
              {status.msg}
            </div>
          )}
          <div style={{ marginLeft: "auto" }}>
            <button
              className="btn btn-secondary btn-sm"
              disabled={!canPreview}
              onClick={runPreview}
            >
              {busy === "preview" ? "Previewing…" : "Preview"}
            </button>
          </div>
        </div>

        {/* Preview panel → confirm */}
        {preview && (
          <div style={{ marginTop: "1.5rem", borderTop: "1px solid #eee", paddingTop: "1rem" }}>
            <h4 style={{ fontWeight: "bold" }}>Review — nothing written yet</h4>

            {preview.warnings && preview.warnings.length > 0 && (
              <div className="alert alert-warning">
                <ul style={{ marginBottom: 0 }}>
                  {preview.warnings.map((w: string, i: number) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </div>
            )}

            <table className="table table-sm">
              <tbody>
                <tr>
                  <th>SPID</th>
                  <td>
                    <code>{preview.spid}</code>
                  </td>
                </tr>
                <tr>
                  <th>Firestore product doc</th>
                  <td>
                    {preview.artifacts.firestore_product.action} &middot; FPID{" "}
                    {preview.artifacts.firestore_product.fpid}
                    <br />
                    <small className="text-muted">
                      {["category", "collection", "country", "industry", "product_type"]
                        .filter((k) => preview.artifacts.firestore_product.fields[k] !== undefined)
                        .map((k) => `${k}: ${preview.artifacts.firestore_product.fields[k]}`)
                        .join(" · ")}
                    </small>
                  </td>
                </tr>
                <tr>
                  <th>Shopify Page</th>
                  <td>
                    {preview.artifacts.shopify_page.action} &middot;{" "}
                    <code>/pages/{preview.artifacts.shopify_page.handle}</code> &middot; template{" "}
                    {preview.artifacts.shopify_page.template_suffix}
                  </td>
                </tr>
                <tr>
                  <th>Embed metafield</th>
                  <td>
                    {preview.artifacts.embed_metafield.action} &middot;{" "}
                    <code>
                      {preview.artifacts.embed_metafield.namespace}.
                      {preview.artifacts.embed_metafield.key}
                    </code>{" "}
                    = {preview.artifacts.embed_metafield.value}
                  </td>
                </tr>
                <tr>
                  <th>Page type metafield</th>
                  <td>
                    {preview.artifacts.page_type_metafield.action} &middot;{" "}
                    <code>
                      {preview.artifacts.page_type_metafield.namespace}.
                      {preview.artifacts.page_type_metafield.key}
                    </code>{" "}
                    = {preview.artifacts.page_type_metafield.value}
                  </td>
                </tr>
                {preview.artifacts.display_metafields && preview.artifacts.display_metafields.length > 0 && (
                  <tr>
                    <th>Display metafields</th>
                    <td>
                      {preview.artifacts.display_metafields
                        .map((m: any) => `${m.key} = ${m.value}`)
                        .join(" · ")}
                    </td>
                  </tr>
                )}
                <tr>
                  <th>Who sees it</th>
                  <td>
                    {isShared ? (
                      <>
                        Clients who have:{" "}
                        <strong>
                          {selectedSourceProducts.map((p) => p.name).join(", ") || "(none chosen yet!)"}
                        </strong>
                        {navAnchorTag ? (
                          <>
                            {" "}
                            · menu link under "{selectedSourceProducts.find((p) => p.tag === navAnchorTag)?.name}"
                          </>
                        ) : (
                          <> · no menu link</>
                        )}
                      </>
                    ) : (
                      <>Only the chosen client — you'll grant them access after creating (link below)</>
                    )}
                    {makePublic ? (
                      <div className="text-danger">Also visible to every signed-in client (public).</div>
                    ) : null}
                  </td>
                </tr>
                <tr>
                  <th>Employee menu item</th>
                  <td>
                    {preview.artifacts.employee_menu_item.action} &middot; menu{" "}
                    <code>{preview.artifacts.employee_menu_item.menu_handle}</code>
                    {preview.artifacts.employee_menu_item.url ? (
                      <>
                        {" "}
                        &middot; <code>{preview.artifacts.employee_menu_item.url}</code>
                      </>
                    ) : (
                      <> &middot; {preview.artifacts.employee_menu_item.note}</>
                    )}
                  </td>
                </tr>
                <tr>
                  <th>Client grant</th>
                  <td>
                    {preview.artifacts.grant.action} &middot; {preview.artifacts.grant.note}
                  </td>
                </tr>
              </tbody>
            </table>

            {preview.artifacts.theme_edits && (
              <div className="alert alert-info">
                <strong>Theme edits</strong> — written to the LIVE theme only when you click{" "}
                <em>Apply theme edits</em> after create (with a rollback backup):
                {"error" in preview.artifacts.theme_edits ? (
                  <div className="alert alert-warning" style={{ marginTop: "0.5rem", marginBottom: 0 }}>
                    Could not compute theme edits: {preview.artifacts.theme_edits.error}
                  </div>
                ) : (
                  <ul style={{ marginBottom: 0, marginTop: "0.5rem" }}>
                    {["template", "gate", "nav"].map((k) => {
                      const e = preview.artifacts.theme_edits[k];
                      return (
                        <li key={k}>
                          <code>{e.key}</code> — <strong>{e.action}</strong>
                          {e.added ? (
                            <details style={{ marginTop: "0.25rem" }}>
                              <summary className="text-muted">show added lines</summary>
                              <pre
                                style={{
                                  whiteSpace: "pre-wrap",
                                  background: "#f7f7f7",
                                  padding: "0.5rem",
                                  marginTop: "0.25rem",
                                }}
                              >
                                {e.added}
                              </pre>
                            </details>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                className="btn btn-primary btn-sm"
                disabled={busy === "create"}
                onClick={runCreate}
              >
                {busy === "create" ? "Creating…" : "Confirm & create"}
              </button>
            </div>
          </div>
        )}

        {/* Result panel */}
        {result && result.status === "success" && (
          <div style={{ marginTop: "1.5rem", borderTop: "1px solid #eee", paddingTop: "1rem" }}>
            <h4 style={{ fontWeight: "bold" }}>Created</h4>
            <p style={{ marginBottom: "0.25rem" }}>
              <strong>SPID</strong> <code>{result.spid}</code> &middot; <strong>FPID</strong>{" "}
              {result.fpid} &middot; <strong>Page</strong> <code>/pages/{result.handle}</code>
            </p>
            <p className="text-muted" style={{ marginBottom: 0 }}>
              provisioning_log: {result.provisioning_log_id}
            </p>

            {/* Grant access — routed through the existing New Access screen (the complete flow:
                access record + expiration + Shopify tags + RLS). */}
            <div className="alert alert-info" style={{ marginTop: "1rem", marginBottom: 0 }}>
              {isShared ? (
                <>
                  <strong>No access grant needed.</strong> This page shows automatically for clients who
                  already have the source dashboards you selected.
                </>
              ) : (
                <>
                  <strong>Give a client access.</strong> This product is now in UMA's product list. To grant{" "}
                  <strong>{companyName || "this client"}</strong>, open the{" "}
                  <Link to={`/company/${companyId}/new-access`}>New Access screen</Link> and choose{" "}
                  "{dashboardName}". To grant a different client later, go to that client's company page →
                  New Access and pick the same product.
                </>
              )}
            </div>

            {/* Theme edits — the separate, developer-gated LIVE write. */}
            <div style={{ marginTop: "1rem", borderTop: "1px dashed #eee", paddingTop: "1rem" }}>
              <strong>Theme edits</strong>{" "}
              <span className="text-muted">(not written yet — this writes to the LIVE theme)</span>
              {result.artifacts?.theme_edits && !("error" in result.artifacts.theme_edits) ? (
                <ul style={{ marginTop: "0.5rem" }}>
                  {["template", "gate", "nav"].map((k) => {
                    const e = result.artifacts.theme_edits[k];
                    return (
                      <li key={k}>
                        <code>{e.key}</code> — <strong>{e.action}</strong>
                      </li>
                    );
                  })}
                </ul>
              ) : result.artifacts?.theme_edits?.error ? (
                <div className="alert alert-warning" style={{ marginTop: "0.5rem" }}>
                  Could not compute theme edits: {result.artifacts.theme_edits.error}
                </div>
              ) : null}

              {themeResult && themeResult.status === "success" && (
                <div className="alert alert-success" style={{ marginBottom: "0.5rem" }}>
                  Applied to theme {themeResult.theme_id}:{" "}
                  {["template", "gate", "nav"]
                    .map((k) => `${themeResult.artifacts?.[k]?.key} → ${themeResult.artifacts?.[k]?.action}`)
                    .join(" · ")}
                  <br />
                  <small className="text-muted">provisioning_log: {themeResult.provisioning_log_id}</small>
                </div>
              )}

              <button
                className="btn btn-primary btn-sm"
                disabled={themeBusy || Boolean(themeResult && themeResult.status === "success")}
                onClick={runApplyTheme}
              >
                {themeBusy
                  ? "Applying…"
                  : themeResult && themeResult.status === "success"
                  ? "Applied ✓"
                  : "Apply theme edits (writes to LIVE)"}
              </button>
            </div>

            {/* Optional: add a resource button on source dashboard page(s) → this new page */}
            <div style={{ marginTop: "1rem", borderTop: "1px dashed #eee", paddingTop: "1rem" }}>
              <strong>Add a button on other dashboards</strong>{" "}
              <span className="text-muted">
                (optional — put a "{resourceDisplayText || "Data Download"}" button that opens this new page
                on existing dashboard pages)
              </span>
              <div className="form-group" style={{ marginTop: "0.5rem" }}>
                <label htmlFor="np-res-handles">
                  Which dashboard pages? <span className="text-muted">(their page address — the part after
                  /pages/ — one per line)</span>
                </label>
                <textarea
                  id="np-res-handles"
                  className="form-control"
                  rows={2}
                  placeholder={"us-cannabis-brand-health-retail-all-states\nus-cannabis-brand-health-retail-dashboard"}
                  value={resourceHandlesText}
                  onChange={(e) => setResourceHandlesText(e.target.value)}
                />
              </div>
              <div style={{ display: "flex", gap: "1rem" }}>
                <div className="form-group" style={{ flex: 2 }}>
                  <label htmlFor="np-res-text">Button label</label>
                  <input
                    id="np-res-text"
                    type="text"
                    className="form-control"
                    value={resourceDisplayText}
                    onChange={(e) => setResourceDisplayText(e.target.value)}
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="np-res-slot">Position</label>
                  <select
                    id="np-res-slot"
                    className="form-control"
                    value={resourceSlot}
                    onChange={(e) => setResourceSlot(e.target.value)}
                  >
                    <option value="auto">Automatic (first free spot)</option>
                    <option value="resource_1">Position 1</option>
                    <option value="resource_2">Position 2</option>
                    <option value="resource_3">Position 3</option>
                  </select>
                </div>
              </div>

              {resourcePreview && resourcePreview.results && (
                <div className="alert alert-info" style={{ marginBottom: "0.5rem" }}>
                  <ul style={{ marginBottom: 0 }}>
                    {resourcePreview.results.map((r: any, i: number) => (
                      <li key={i}>
                        <code>{r.handle}</code> — {r.status}
                        {r.slot ? (
                          <>
                            {" "}
                            → <strong>{r.slot}</strong>
                          </>
                        ) : null}
                        {r.will_overwrite ? (
                          <span className="text-danger"> (would overwrite "{r.current?.text}")</span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {resourceResult && resourceResult.results && (
                <div
                  className={`alert alert-${resourceResult.status === "success" ? "success" : "danger"}`}
                  style={{ marginBottom: "0.5rem" }}
                >
                  {resourceResult.results
                    .map((r: any) => `${r.handle}: ${r.status}${r.slot ? " (" + r.slot + ")" : ""}`)
                    .join(" · ")}
                </div>
              )}

              <div style={{ display: "flex", gap: "0.5rem" }}>
                <button
                  className="btn btn-secondary btn-sm"
                  disabled={!resourceHandles.length || Boolean(resourceBusy)}
                  onClick={() => runResource(true)}
                >
                  {resourceBusy === "preview" ? "Previewing…" : "Preview"}
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  disabled={!resourcePreview || Boolean(resourceBusy)}
                  onClick={() => runResource(false)}
                >
                  {resourceBusy === "set" ? "Adding…" : "Add the button"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
